import { Observable } from 'rxjs';
import { EventEmitter, Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GlobalLoadingService } from '../../global-services/global-loading.service';
import { GlobalMessageService } from '../../global-services/global-message.service';

export interface PatientConsentUiState {
  form: FormGroup;
}

@Injectable({ providedIn: 'root' })
export class PatientConsentFacade {
  public uiState: PatientConsentUiState;

  private uiStateEvent: EventEmitter<PatientConsentUiState> = new EventEmitter<PatientConsentUiState>();
  public uiState$: Observable<PatientConsentUiState> = this.uiStateEvent.asObservable();

  constructor(
    private formBuilder: FormBuilder,
    private loadingService: GlobalLoadingService,
    private messageService: GlobalMessageService,
  ) {
    this.uiState = {
      form: this.createForm(),
    };
  }

  public initializeComponent(): void {
    this.emitUiState();
  }

  public onClose(): void {
    // Handle close logic - could navigate away or emit close event
    this.messageService.showInfo('Info', 'Consent form closed');
  }

  public updateConsentValue(value: string): void {
    this.uiState.form.get('consentStatus')?.setValue(value);
  }

  public async saveConsent(): Promise<void> {
    // Mark all fields as touched to trigger validation display
    this.uiState.form.markAllAsTouched();

    const consentValue = this.uiState.form.get('consentStatus')?.value;

    if (!this.uiState.form.valid) {
      this.messageService.showError('Error', 'Please select a consent option');
      return;
    }

    try {
      this.loadingService.show();

      const consentStatus = this.uiState.form.get('consentStatus')?.value;

      // Validate that a specific value is selected (not just empty string)
      if (!consentStatus || (consentStatus !== 'given' && consentStatus !== 'not-given')) {
        this.messageService.showError('Error', 'Please select a valid consent option');
        return;
      }

      // Here you would typically call a service to save the consent
      // await this.patientService.saveConsent({ consentStatus });

      this.messageService.showSuccess('Success', `Patient consent ${consentStatus === 'given' ? 'granted' : 'declined'} and recorded successfully`);

      // Reset form or navigate away
      this.resetForm();

      return Promise.resolve();
    } catch (error) {
      this.messageService.showError('Error', 'Failed to save consent. Please try again.');
      return Promise.reject(error);
    } finally {
      this.loadingService.hide();
    }
  }

  private createForm(): FormGroup {
    return this.formBuilder.group({
      consentStatus: ['', Validators.required],
    });
  }

  private resetForm(): void {
    this.uiState.form = this.createForm();
    this.emitUiState();
  }

  private emitUiState(): void {
    this.uiStateEvent.emit(this.uiState);
  }
}
